import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Maximize, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import applySessionEvent, { eventIsStale, eventRevealsMissedState } from '../applySessionEvent';
import { sprekersApi } from '../api';
import CanvasPlayerStage from '../canvas/CanvasPlayerStage';
import { resolveIsOverviewAndTarget } from '../canvas/canvasSelectors';
import useLiveSessionState from '../hooks/useLiveSessionState';
import useLiveSessionStream from '../hooks/useLiveSessionStream';
import { persistLiveSession, readLiveSession } from '../liveSessionStorage';
import { computeLiveDisplayStatus, LIVE_DISPLAY_STATUS_LABELS } from '../liveStatus';
import SlideRenderer from '../components/SlideRenderer';
import type { CanvasBoardDetail, PresentationDetail, PresentationSession } from '../types';

/**
 * Het beamerscherm (sectie 16.2 van docs/sprekers-Module.md): toont
 * uitsluitend de actieve slide, nooit bedieningselementen of privé-inhoud.
 * Bediening loopt via een los gekoppelde telefoon (QR, sectie 16.5) of een
 * tweede tabblad met LiveControlPage (sectie 17.1) -- nooit op dit scherm
 * zelf.
 */
export default function LivePresenterPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { session, sessionRef, updateSession } = useLiveSessionState(() => {
    // sessionStorage weegt zwaarder dan location.state: de browser bewaart
    // history.state (waar location.state op leunt) ook over een reload van
    // dezelfde URL heen, maar dan met de VEROUDERDE momentopname van het
    // moment van aanmaken -- sessionStorage wordt bij elke wijziging bijgewerkt
    // en is dus na een reload altijd minstens even actueel.
    const fromStorage = sessionId ? readLiveSession(sessionId) : null;
    const fromNav = (location.state as { session?: PresentationSession } | null)?.session;
    return fromStorage || fromNav || null;
  });
  const [presentation, setPresentation] = useState<PresentationDetail | null>(null);
  const [canvasBoard, setCanvasBoard] = useState<CanvasBoardDetail | null>(null);
  const [isOverview, setIsOverview] = useState(false);
  const [creatingNewSession, setCreatingNewSession] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (session) persistLiveSession(session);
  }, [session]);

  useEffect(() => {
    if (!session) return;
    sprekersApi.getPresentation(session.speaking_engagement_id).then(setPresentation).catch(() => setPresentation(null));
  }, [session?.speaking_engagement_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!presentation || presentation.presentation_type !== 'canvas') {
      setCanvasBoard(null);
      return;
    }
    sprekersApi.getCanvasBoard(presentation.id).then(setCanvasBoard).catch(() => setCanvasBoard(null));
  }, [presentation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Alleen initialiseren zodra het board binnenkomt (vóór het eerste
    // SSE-event) -- latere presentation.focus_changed-events werken
    // isOverview zelf bij via de is_overview-vlag uit het event.
    if (!canvasBoard || !session) return;
    setIsOverview(
      resolveIsOverviewAndTarget(canvasBoard.path, session.current_path_index, session.canvas_manual_overview).isOverview,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasBoard]);

  const streamStatus = useLiveSessionStream(session?.status === 'beeindigd' || session?.status === 'verlopen' ? null : session?.id, (event) => {
    // Een snapshot (bij elke (her)verbinding) of delta die ouder is dan onze
    // toestand mag ook de overzichtsvlag niet terugzetten.
    const stale = eventIsStale(sessionRef.current, event);
    updateSession((prev) => (prev ? applySessionEvent(prev, event) : prev));
    if (
      !stale
      && (event.type === 'presentation.focus_changed' || event.type === 'presentation.state')
      && typeof event.is_overview === 'boolean'
    ) {
      setIsOverview(event.is_overview);
    }
  }, {
    needsResync: (event) => eventRevealsMissedState(sessionRef.current, event),
    retryKey,
  });

  const currentSlide = useMemo(
    () => presentation?.slides.find((slide) => slide.id === session?.current_slide_id) || null,
    [presentation, session?.current_slide_id],
  );

  const pairingUrl = session?.session_token
    ? `${window.location.origin}/sprekers/sessies/${session.id}/koppelen?token=${session.session_token}`
    : null;

  const enterFullscreen = () => { if (!document.fullscreenElement) document.documentElement.requestFullscreen?.(); };

  const displayStatus = computeLiveDisplayStatus(session, streamStatus);

  const startNewSession = async () => {
    if (!session) return;
    setCreatingNewSession(true);
    try {
      const fresh = await sprekersApi.createSession(session.presentation_id);
      persistLiveSession(fresh);
      navigate(`/sprekers/sessies/${fresh.id}/presenteren`, { replace: true, state: { session: fresh } });
      updateSession(() => fresh);
      setPresentation(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Nieuwe sessie starten mislukt');
    } finally {
      setCreatingNewSession(false);
    }
  };

  if (!session || !sessionId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6 text-center text-slate-300">
        <p>Geen sessiegegevens gevonden op dit apparaat. Start de live modus opnieuw vanaf het presentatietabblad.</p>
      </div>
    );
  }

  if (displayStatus === 'beeindigd' || displayStatus === 'verlopen' || displayStatus === 'onherstelbaar') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-950 p-6 text-center text-white">
        <p className="text-2xl text-slate-200">{LIVE_DISPLAY_STATUS_LABELS[displayStatus]}</p>
        <Button onClick={startNewSession} disabled={creatingNewSession}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Nieuwe sessie en QR-code genereren
        </Button>
      </div>
    );
  }

  if (displayStatus === 'wacht_op_koppeling') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-950 p-6 text-center text-white">
        <button
          type="button" onClick={enterFullscreen} aria-label="Volledig scherm"
          className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white/70 hover:bg-white/20"
        >
          <Maximize className="h-5 w-5" />
        </button>
        <p className="text-xl font-medium text-slate-200">Scan de QR-code met de telefoon van de spreker om te koppelen</p>
        {pairingUrl ? (
          <div className="rounded-2xl bg-white p-6">
            <QRCodeSVG value={pairingUrl} size={280} level="M" marginSize={0} />
          </div>
        ) : (
          <p className="max-w-md text-sm text-slate-400">
            De koppelcode is alleen zichtbaar op het apparaat waar de live sessie is gestart.
          </p>
        )}
        <p className="text-sm text-slate-500">{LIVE_DISPLAY_STATUS_LABELS.wacht_op_koppeling}...</p>
      </div>
    );
  }

  // 'gekoppeld' (verbonden) of 'verbroken' (tijdelijk, browser probeert zelf
  // opnieuw te verbinden): in beide gevallen blijft de laatst bekende slide
  // gewoon zichtbaar -- sectie 16.2 wil hier uitsluitend slide-inhoud, dus
  // een korte onderbreking mag het beamerscherm niet laten leeglopen. Alleen
  // een klein, niet-opdringerig statuspuntje maakt de verbindingsstatus nog
  // zichtbaar zonder de projectie te verstoren.
  return (
    <div className="relative h-screen w-screen bg-black">
      {presentation?.presentation_type === 'canvas' && canvasBoard ? (
        <CanvasPlayerStage
          board={canvasBoard}
          currentItemId={session.current_item_id}
          isOverview={isOverview}
          blackout={session.is_blackout}
        />
      ) : (
        <SlideRenderer slide={currentSlide} blackout={session.is_blackout} size="full" />
      )}
      {(displayStatus === 'verbroken' || displayStatus === 'verbroken_gestopt') && (
        <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs text-amber-300">
          <span className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
          {LIVE_DISPLAY_STATUS_LABELS[displayStatus]}
          {displayStatus === 'verbroken_gestopt' && (
            <button
              type="button" onClick={() => setRetryKey((key) => key + 1)}
              className="rounded-full bg-amber-300 px-2 py-0.5 font-medium text-black hover:bg-amber-200"
            >
              Opnieuw verbinden
            </button>
          )}
        </div>
      )}
    </div>
  );
}
